import { FunctionCoreEngine } from './function.core.engine';
import { MeasModeItem } from './measure.mode.obj';

/**
 * Classe utilitaire des opérations mathématiques
 */
export class MathUtils {
  /** Constante définissant l'opérateur d'addition */
  public static readonly OPERATOR_ADD: string = '+';
  /** Constante définissant l'opérateur de soustraction */
  public static readonly OPERATOR_SUBSTRACT: string = '-';
  /** Constante définissant l'opérateur de multiplication */
  public static readonly OPERATOR_MULTIPLY: string = '*';
  /** Constante définissant l'opérateur de division */
  public static readonly OPERATOR_DIVIDE: string = '/';

  /** Constante définissant le comparateur d'égalité */
  public static readonly COMPARATOR_EQUAL: string = '=';
  /** Constante définissant le comparateur de différence */
  public static readonly COMPARATOR_NOT_EQUAL: string = '<>';
  /** Constante définissant le comparateur d'infériorité stricte */
  public static readonly COMPARATOR_LESS: string = '<';
  /** Constante définissant le comparateur d'infériorité non stricte */
  public static readonly COMPARATOR_LESS_EQUAL: string = '<=';
  /** Constante définissant le comparateur de supériorité stricte */
  public static readonly COMPARATOR_MORE: string = '>';
  /** Constante définissant le comparateur de supériorité non stricte */
  public static readonly COMPARATOR_MORE_EQUAL: string = '>=';

  /** marqueur d'erreur : # */
  public static readonly ERROR_TAG: string = '#';

  /** l'ordre des opérateurs */
  private static readonly OPERATORLIST: string[] = [
    MathUtils.OPERATOR_DIVIDE,
    MathUtils.OPERATOR_MULTIPLY,
    MathUtils.OPERATOR_SUBSTRACT,
    MathUtils.OPERATOR_ADD,
  ];

  /**
   * calcul sur opérateur
   * @param a        Premier opérande
   * @param operator Opérateur à appliquer
   * @param b        Second opérande
   * @return String Résultat de l'opération
   */
  private static mathBasicEval(a: string, operator: string, b: string): string {
    let r: number = 0;

    const cleanA: number = isNaN(Number(a)) ? 0 : Number(a);
    const cleanB: number = isNaN(Number(b)) ? 0 : Number(b);

    switch (operator) {
      case MathUtils.OPERATOR_DIVIDE:
        if (cleanB === 0) {
          return '#DIV0';
        } else {
          r += Math.round((cleanA * 100000) / cleanB) / 100000;
        }
        break;
      case MathUtils.OPERATOR_MULTIPLY:
        r += Math.round(cleanA * 100000 * cleanB) / 100000;
        break;
      case MathUtils.OPERATOR_SUBSTRACT:
        r += Math.round((cleanA - cleanB) * 100000) / 100000;
        break;
      case MathUtils.OPERATOR_ADD:
        r += Math.round((cleanA + cleanB) * 100000) / 100000;
        break;
    }
    return r.toString();
  }

  /**
   * Calcul sur comparateur
   *
   * @param a          Elément à comparer
   * @param comparator Comparateur à appliquer
   * @param b          Elément de comparaison
   * @return String '1' si comparaison est satisfaite, '0' dans le cas contraire
   */
  private static mathCompareEval(a: string, comparator: string, b: string): string {
    let result: string = null; // '0';
    // 0: si a = b, -1 si a < b, 1 si a> b
    let compare: number = 0;
    // interception des comparaisons de chaine sur "=" ou "!="
    if (isNaN(Number(a)) && isNaN(Number(b))) {
      // 2 chaines de texte
      // console.log('compare string', a, b);
      compare = -1; // chaines de texte, on considère qu'elles sont différentes
      if (comparator === MathUtils.COMPARATOR_EQUAL || comparator === MathUtils.COMPARATOR_NOT_EQUAL) {
        // avec = ou <>
        if (a === b) {
          compare = 0;
        }
      }
    } else {
      if (!isNaN(Number(a)) && !isNaN(Number(b))) {
        // au moins un number
        if (Number(a) > Number(b)) {
          compare = 1;
        } else if (Number(a) < Number(b)) {
          compare = -1;
        }
      } else {
        compare = null;
      }
    }

    switch (comparator) {
      case MathUtils.COMPARATOR_EQUAL:
        result = compare === 0 ? '1' : '0';
        break;
      case MathUtils.COMPARATOR_NOT_EQUAL:
        result = compare === 0 ? '0' : '1';
        break;
      case MathUtils.COMPARATOR_LESS:
        result = compare === -1 ? '1' : '0';
        break;
      case MathUtils.COMPARATOR_LESS_EQUAL:
        result = compare <= 0 ? '1' : '0';
        break;
      case MathUtils.COMPARATOR_MORE:
        result = compare === 1 ? '1' : '0';
        break;
      case MathUtils.COMPARATOR_MORE_EQUAL:
        result = compare >= 0 ? '1' : '0';
        break;
    }
    return result;
  }

  /**
   * construction du résultat (Bigdecimal) sur la base d'une liste de valeurs / opérateurs
   * Le résultat est alors fourni dans l'élément de mesure fourni en entrée
   *
   * @param parentMmi Element de mesure contenant les valeurs et opérations à réaliser
   */
  public static computeMathParam(parentMmi: MeasModeItem): void {
    const mmiList: MeasModeItem[] = parentMmi.c;

    let params: string[] = []; // les paramètres de l'expression mathématique
    let idx: number = 0;

    mmiList.forEach((mmi: MeasModeItem) => {
      if (mmi.ftxt) { // pas de traitement sur les textes, on les prend tels quels
        params[idx] = mmi.r;
      } else if (mmi.fOper || mmi.fComp) {
        // Si opérateur ou comparateur
        params[idx] = mmi.form;
      } else {
        try {
          // numérique... (TODO ou pas si cellules de texte)
          if (mmi.r === null || mmi.r === undefined) {
            // System.out.println("math error: "+ mmi.getResult()+" "+
            // mmi.getMeasureMode().getFormula());
            // console.log('computeMathParam #NA', mmi);
            mmi.r = FunctionCoreEngine.FUNCTION_NOT_APPLICABLE;
            mmi.isErr = true;
            // parentMmi.hasError = true;
          } else if (!mmi.ftxt && mmi.r.toString().indexOf(MathUtils.ERROR_TAG) > 0) {
            // sauf si c'est une erreur : #xx
            mmi.mm.isErr = true;
            mmi.errC = mmi.r;
            // console.log('computeMathParam #NA', mmi);
            parentMmi.r = mmi.r;
            parentMmi.isErr = true;

            params[idx] = mmi.r;

          } else {
            params[idx] = mmi.r;
          }
        } catch(e: any) {
          console.log('mathUtils', mmi.r);
        }

      }
      idx++;
    });

    // console.log('Math', parentMmi.rawFormula, mmiList, params);
    // if (parentMmi.rawFormula === '=-10') {
    //   console.log('hit');
    // }
    MathUtils.OPERATORLIST.forEach((operator) => {
      // on cherche des opérateurs dans les params
      params = this.computeMathOperator(operator, params);
    });

    // en mode comparator, il doit rester 3 éléments
    if (params.length === 3) {
      params = [this.mathCompareEval(params[0], params[1], params[2])];
    }

    // System.out.println(Arrays.toString(params)+"\n");
    // il ne reste qu'un seul paramètre, c'est un BigD
    parentMmi.r = params[0];
  }

  /**
   * Construction du résultat d'une opération
   * Lorsque l'index d'arrivée est à 0, on a fini de traiter les paramètres de l'opération
   *
   * @param operator Opérateur concerné
   * @param params   Les paramètres de l'opération
   * @return String[] Les paramètres avec le résultat de l'opétation (indice = 0)
   */
  private static computeMathOperator(operator: string, params: string[]): string[] {
    const result: string[] = params;
    let idx: number = 0;

    while (idx < result.length) {
      if (operator === result[idx]) {
        if (idx === 0) { // pas de neutralisation des '-+' injectés comme du texte
          if (params.length === 1) { // on ne fait rien, c'est OK
            idx++;
          } else {
              // gestion du -+ en début de chaine
              if (operator === MathUtils.OPERATOR_SUBSTRACT) {
                // console.log('startWith -', params);
                result[idx] = (-Number(params[idx + 1])).toString(); // negate
                result.splice(idx + 1, 1);
                idx++;
              } else if (operator === MathUtils.OPERATOR_ADD) {
                // console.log('startWith +', params);
                result[idx] = params[idx + 1];
                result.splice(idx + 1, 1);
                idx++;
              }
          }
        } else {
          // TODO interception div0
          result[idx - 1] = this.mathBasicEval(result[idx - 1], result[idx], result[idx + 1]);
          result[idx] = null;
          result[idx + 1] = null;
          result.splice(idx, 2);
          const slice1: string[] = result.slice(0, idx);
          idx--;
        }
      } else {
        idx++;
      }
    }
    return result;
  }

  /**
   * comparateur custom pour sort correct sur les values, pas les rendus formatés
   * @param valueA
   * @param valueB
   */
  public static sortComparator(valueA, valueB): number {
    let result: number = 0;
    if (valueA || valueB) {
      if (valueA && valueB) {
        if (isNaN(valueA) || isNaN(valueB)) {
          // a minima un texte
          result = String(valueA).localeCompare(String(valueB));
        } else {
          // 2 numbers
          if (Number(valueA) > Number(valueB)) {
            result = 1;
          } else if (Number(valueA) < Number(valueB)) {
            result = -1;
          }
        }
      } else {
        result = valueA ? 1 : -1;
      }
    }
    return result;
  }


  public static invertColor(hex): any {
    if (hex.indexOf('#') === 0) {
        hex = hex.slice(1);
    }
    // convert 3-digit hex to 6-digits.
    if (hex.length === 3) {
        hex = hex[0] + hex[0] + hex[1] + hex[1] + hex[2] + hex[2];
    }
    if (hex.length !== 6) {
        return '#000000';
        //throw new Error('Invalid HEX color.');
    }
    // invert color components
    var r = (255 - parseInt(hex.slice(0, 2), 16)).toString(16),
        g = (255 - parseInt(hex.slice(2, 4), 16)).toString(16),
        b = (255 - parseInt(hex.slice(4, 6), 16)).toString(16);
    // pad each with zeros and return
    return '#' + this.padZero(r) + this.padZero(g) + this.padZero(b);
}

public static padZero(str, len?) {
    len = len || 2;
    var zeros = new Array(len).join('0');
    return (zeros + str).slice(-len);
}

/**
 * conversion rgba en hexa, en cas d'Hexa en entrée, pas de soucis
 * @param orig 
 * @returns 
 */
public static RGBA2HEX(orig): string {
  let a: any;
  let rgb = orig.replace(/\s/g, '').match(/^rgba?\((\d+),(\d+),(\d+),?([^,\s)]+)?/i);
  let alpha = (rgb && rgb[4] || "").trim();
  let hex = rgb ?
  (rgb[1] | 1 << 8).toString(16).slice(1) +
  (rgb[2] | 1 << 8).toString(16).slice(1) +
  (rgb[3] | 1 << 8).toString(16).slice(1) : orig;

  if (alpha !== "") {
    hex = '#'+ hex + (alpha*100).toString(16);
  }
  return hex;
}


}
